import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Database } from 'lucide-react';
import { Button } from '../components/ui/Button.js';
import { Field } from '../components/ui/Field.js';
import { Alert, Card } from '../components/ui/Card.js';
import { useAuth } from '../lib/auth.js';
import { ApiRequestError } from '../lib/api.js';

interface LocationState {
  from?: string;
}

export function Login() {
  const { login, registrationOpen } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Return the learner to whatever they were trying to reach before the
  // redirect, rather than dumping everyone on the dashboard.
  const redirectTo = (location.state as LocationState | null)?.from ?? '/dashboard';

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    const form = new FormData(event.currentTarget);
    const email = String(form.get('email') ?? '')
      .trim()
      .toLowerCase();
    const password = String(form.get('password') ?? '');

    if (!email || !password) {
      setFormError('Enter your email and password.');
      return;
    }

    setIsSubmitting(true);
    try {
      await login({ email, password });
      void navigate(redirectTo, { replace: true });
    } catch (error) {
      setFormError(
        error instanceof ApiRequestError ? error.message : 'Could not sign in. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-12 dark:bg-slate-950">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <Database className="size-6 text-brand-600 dark:text-brand-400" aria-hidden="true" />
          <span className="font-semibold text-slate-900 dark:text-slate-100">pgLearn</span>
        </Link>

        <Card className="p-8">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Welcome back</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Pick up where you left off.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
            {formError && <Alert>{formError}</Alert>}

            <Field label="Email" name="email" type="email" autoComplete="email" required />
            <Field
              label="Password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              revealable
            />

            <Button type="submit" className="w-full" isLoading={isSubmitting}>
              Sign in
            </Button>
          </form>

          {registrationOpen !== false && (
            <p className="mt-6 text-center text-sm text-slate-600 dark:text-slate-400">
              New here?{' '}
              <Link
                to="/register"
                className="font-medium text-brand-600 hover:underline dark:text-brand-400"
              >
                Create an account
              </Link>
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}
